import PropTypes from 'prop-types';
import React from 'react';
import {events} from 'lib';

import {find, findAll, replace} from 'api/dataCleanup';

import {Card} from 'components';
import {Pagination, PageFooter, ConfirmationModal, Button, Icon, Table} from 'components/layout';
import {Select, Input, BranchSelect} from 'components/forms';
import Callout from 'components/tools/Callout';

import ValidateGroup from 'helpers/validation';
import downloadCSV from 'helpers/csvHelpers';
import debounce from 'lodash/debounce';

import {FormattedMessage, FormattedNumber, intlShape, injectIntl} from 'react-intl';

const DISMISS_FIND_REPLACE_CONFIRMATION = 'DISMISS_FIND_REPLACE_CONFIRMATION';

function toUpperCamel(xs) {
  return xs[0].toUpperCase() + xs.slice(1);
}

let replaceFields;

class FindReplacePage extends React.Component {
  static displayName = 'FindReplacePage';

  static propTypes = {
    intl: intlShape.isRequired,
  };

  state = {
    all: undefined,
    confirmAction: null,
    confirmMessage: '',
    confirmVisible: false,
    field: undefined,
    find: undefined,
    page: 1,
    partial: undefined,
    replace: undefined,
    results: [],
    sortDirection: 'asc',
    sortKey: 'name',
    store: undefined,
  };

  componentWillMount() {
    let {formatMessage} = this.props.intl;

    this.group = new ValidateGroup(() => {
      this.setState({
        replace: undefined,
        page: 1,
      }, this._find);
    });
    this.updateReplace = debounce(({value}) => {
      this.setState({replace: value});
    }, 500);

    replaceFields = new Map([
      ['', formatMessage({id: 'tools.select.field'})],
      ['nameFirst', formatMessage({id: 'general.first.name'})],
      ['nameLast', formatMessage({id: 'general.last.name'})],
      ['email', formatMessage({id: 'general.email'})],
      ['homePhone', formatMessage({id: 'general.phone.number'})],
      ['contactOrganization', formatMessage({id: 'customer.organization'})],
      ['address1', formatMessage({id: 'general.address'})],
      ['city', formatMessage({id: 'general.city'})],
      ['state', formatMessage({id: 'general.state'})],
      ['zip', formatMessage({id: 'general.zipcode'})],
    ]);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.page !== this.state.page || prevState.sortKey !== this.state.sortKey || prevState.sortDirection !== this.state.sortDirection) {
      this._find();
    }
  }

  onPageChange = (page) => {
    this.setState({page});
  };

  _find = () => {
    let store = this.branch.getWrappedInstance().getValue();
    let field = this.field.getValue();
    let _find = this.find.getValue() || '';
    let partial = this.partialMatch.getValue();

    find(store, field, _find, partial, this.state.sortKey, this.state.sortDirection, this.state.page)
      .then(x => {
        this.setState({
          results: x.results,
          field,
          find: _find,
          partial: partial,
          total: x.total,
          store,
        });
      });
  };

  _findAll = () => {
    let store = this.branch.getWrappedInstance().getValue();
    let field = this.field.getValue();
    let _find = this.find.getValue() || '';
    let partial = this.partialMatch.getValue();

    return findAll(store, field, _find, partial, this.state.sortKey, this.state.sortDirection);
  };

  closeConfirm = () => {
    this.setState({
      confirmVisible: false,
      confirmAction: null,
      confirmMessage: null,
    });
  };

  export = () => {
    const field = toUpperCamel(this.state.field);
    let {formatMessage} = this.props.intl;
    const fields = [
      'Name',
      'fieldType',
      field,
    ];
    const fieldHeaders = [
      formatMessage({id: 'general.customer'}),
      formatMessage({id: 'tools.field.type'}),
      formatMessage({id: 'tools.find.word'}),
    ];

    this._findAll()
      .then(({results}) => {
        results = results.map(x => {
          x.fieldType = replaceFields.get(this.state.field);
          return x;
        });

        if (this.state.replace) {
          fields.push(formatMessage({id: 'general.preview'}).toLowerCase());
          fieldHeaders.push(formatMessage({id: 'tools.replace.with'}));
          results = results.map(x => {
            // Regexp escape from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
            const escaped = this.state.find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

            x.replace = x[field].replace(new RegExp(escaped, 'ig'), this.state.replace);
            return x;
          });
        }

        downloadCSV(results, fields, fieldHeaders, 'find-and-replace.csv');
      })
      .catch(err => {
        console.error(err);
      });
  };

  openConfirm = (id) => {
    return () => {
      let rec = this.state.results.find(x => x.ID === id);

      this.setState({
        confirmVisible: true,
        confirmAction: this.replace(id),
        confirmMessage: <ReplaceOneConfirm find={this.state.find}
                                           replace={this.state.replace}
                                           accountNumber={rec.AccountNo}
                                           customer={rec.Name}/>,
      });
    };
  };

  openConfirmAll = () => {
    this.setState({
      confirmVisible: true,
      confirmAction: this.replaceAll,
      confirmMessage: <ReplaceAllConfirm find={this.state.find}
                                         replace={this.state.replace}
                                         number={this.state.total}/>,
    });
  };

  print = () => {
    let {formatMessage} = this.props.intl;
    let headers = [
      undefined,
      formatMessage({id: 'general.customer'}),
      formatMessage({id: 'general.match'}),
    ];

    if (this.state.replace) {
      headers.push(formatMessage({id: 'general.preview'}));
    }

    this._findAll()
      .then(({results}) => {
        let rows = results.map(x => {
          let star;
          let flag;

          if (x.fav) {
            star = <i className='bl bl-star-filled fa-lg star'
                      title={formatMessage({id: 'general.preferred.customer'})}/>;
          }
          if (x.flag) {
            flag = <i className='bl bl-flag-filled fa-lg flagged'
                      title={x.flagTitle}/>;
          }

          let field = toUpperCamel(this.state.field);
          let linkID = Object.keys(x).includes('CustomerID') ? x.CustomerID : x.ID;

          return <PreviewResult name={x.Name}
                                idKey={linkID}
                                value={x[field]}
                                key={x.ID}
                                star={star}
                                flag={flag}
                                preview={this.state.replace ?
                                  !this.state.find ?
                                    this.state.replace :
                                    x[field].replace(new RegExp(this.state.find, 'ig'), this.state.replace) :
                                  undefined}/>;
        });

        events.publish('print', <Table headings={headers}
                                       rows={rows}/>);
      })
      .catch(err => {
        console.error(err);
      });
  };

  replace = (id) => {
    return () => {
      replace(this.state.store, this.state.field || '', this.state.find, this.state.partial, this.state.replace, [id])
        .then(() => {
          this.setState({results: this.state.results.filter(x => x.ID !== id), confirmVisible: false});
        });
    };
  };

  replaceAll = () => {
    replace(this.state.store, this.state.field || '', this.state.find, this.state.partial, this.state.replace)
      .then(() => {
        events.publish('success.user', {
          message: <FormattedMessage id="tools.fields.been.changed" values={{count: <Callout>{this.state.total}</Callout>}}/>,
        });
        this.resetState();
      });
  };

  resetState = () => {
    this.setState({
      results: [],
      store: undefined,
      field: undefined,
      replace: undefined,
      find: undefined,
      partial: undefined,
      confirmVisible: false,
      confirmAction: null,
    });
  };

  sort = (key) => {
    if (key !== this.state.sortKey) {
      this.setState({
        sortKey: key,
      });
    } else {
      this.setState({
        sortDirection: this.state.sortDirection === 'asc' ? 'desc' : 'asc',
      });
    }
  };

  render() {
    let {formatMessage} = this.props.intl;
    let headers = [
      undefined,
      {label: formatMessage({id: 'general.customer'}), sortKey: 'name'},
      formatMessage({id: 'tools.field.type'}),
      formatMessage({id: 'tools.find.word'}),
    ];

    if (this.state.replace) {
      headers.push(formatMessage({id: 'tools.replace.with'}));
      headers.push(undefined);
    }

    let results = this.state.results;

    let rows = results.map(x => {
      let flag;
      let star;

      if (x.isTruBlueActive) {
        star = <i className='bl bl-tru-blue-filled fa-lg blue'
                  title={formatMessage({id: 'general.trublue.active'})}/>;
      } else if (x.isTruBlueInactive) {
        star = <i className='bl bl-tru-blue-filled fa-lg grey'
                  title={formatMessage({id: 'general.trublue.inactive'})}/>;
      } else if (x.fav) {
        star = <i className='bl bl-star-filled fa-lg star'
                  title={formatMessage({id: 'general.preferred.customer'})}/>;
      }
      if (x.flag) {
        flag = <i className='bl bl-flag-filled fa-lg flagged'
                  title={x.flagTitle}/>;
      }

      let field = toUpperCamel(this.state.field);

      let linkID = Object.keys(x).includes('CustomerID') ? x.CustomerID : x.ID;

      return <PreviewResult name={x.Name}
                            idKey={linkID}
                            value={x[field]}
                            key={x.ID}
                            replace={this.openConfirm(x.ID)}
                            star={star}
                            flag={flag}
                            field={replaceFields.get(this.state.field)}
                            preview={this.state.replace ?
                              !this.state.find ?
                                this.state.replace :
                                x[field].replace(new RegExp(this.state.find, 'ig'), this.state.replace) :
                              undefined}/>;
    });

    return <div>
      <Card fullWidth>
        <div className='row'>
          <div className='col-md-3'>
            <BranchSelect label={formatMessage({id: 'general.select.store'}) + '(s)'}
                          ref={b => this.branch = b}
                          required
                          group={this.group}
                          onChange={this.resetState}/>
          </div>
          <div className='col-md-2'>
            <Select label={formatMessage({id: 'tools.field.type'})}
                    options={[...replaceFields.values()]}
                    keys={[...replaceFields.keys()]}
                    ref={field => this.field = field}
                    required
                    group={this.group}
                    dataChanged={this.resetState}/>
          </div>
          <div className='col-md-3'>
            <Input label={formatMessage({id: 'general.find'})}
                   ref={find => this.find = find}
                   placeholder={formatMessage({id: 'general.keyword'})}
                   group={this.group}
                   dataChanged={this.resetState}/>
          </div>
          <div className='col-md-2'>
            <Select options={[formatMessage({id: 'tools.exact.match'}), formatMessage({id: 'tools.partial.match'})]}
                    ref={x => this.partialMatch = x}
                    keys={[0, 1]}
                    label={formatMessage({id: 'tools.account.match'})}
                    dataChanged={this.resetState}/>
          </div>
          <div className='col-md-2'>
            <div className='pull-right'>
              <Button default
                      onClick={this.group.validate}
                      style={{marginTop: 20}}>{formatMessage({id: 'general.find'})}</Button>
            </div>
          </div>
        </div>
        {this.state.results.length ? <div>
          <hr/>
          <div className='row'>
            <div className='col-md-5 semi-bold'>
              <i className='bl bl-help-circle blue fa-lg'/>
              <FormattedMessage id="tools.accounts.identified" values={{count: <Callout>{this.state.total}</Callout>}}/>
            </div>
            <div className='col-md-3'>
              <Input label={formatMessage({id: 'tools.replace.with'})}
                     ref={r => this.replaceElement = r}
                     group={this.group}
                     dataChanged={this.updateReplace}/>
            </div>
            <div className='col-md-4'>
              <div className='pull-right'>
                <Button default
                        href='#tools/accounts'
                        style={{marginTop: 20, marginRight: 20}}>{formatMessage({id: 'general.cancel'})}</Button>
                <Button primary
                        onClick={this.openConfirmAll}
                        disabled={!(this.state.replace && this.state.find && this.state.results && this.state.results.length)}
                        style={{marginTop: 20}}><FormattedMessage id="tools.replace.all"/></Button>
              </div>
            </div>
          </div>
        </div> : null}
      </Card>

      <ConfirmationModal visible={this.state.confirmVisible}
                         onSubmit={this.state.confirmAction}
                         onClose={this.closeConfirm}
                         submitLabel='Replace'
                         message={this.state.confirmMessage}
                         dismissKey={DISMISS_FIND_REPLACE_CONFIRMATION}/>

      {this.state.results && this.state.results.length ? <div>
        <div className='row'>
          <div className='col-md-12'>
            <div className='pull-right'>
              <Button link
                      onClick={this.export}
                      style={{marginRight: 10}}><Icon name='export'/><FormattedMessage id="reports.export.report"/></Button>
              <Button link
                      onClick={this.print}><Icon name='print'/><FormattedMessage id="tools.print.report"/></Button>
            </div>
          </div>
        </div>
        <Table headings={headers}
               rows={rows}
               sort={this.sort}
               currentSort={this.state.sortKey}
               sortDirection={this.state.sortDirection}/>
      </div> : <div><FormattedMessage id="general.error.nomatches"/></div>}
      <PageFooter className='tc'>
        <Pagination onPageChange={this.onPageChange}
                    page={this.state.page}
                    count={25}
                    total={this.state.total}/>
      </PageFooter>
    </div>;
  }
}

function ReplaceOneConfirm({find, replace, accountNumber, customer}) {
  return <p>
    <FormattedMessage id='tools.sure.want.replace'
                      values={{
                        old: <Callout>{find}</Callout>,
                        new: <Callout>{replace}</Callout>,
                        customer: `${accountNumber} - ${customer}`,
                      }}/>
  </p>;
}

ReplaceOneConfirm.propTypes = {
  find: PropTypes.string.isRequired,
  replace: PropTypes.string.isRequired,
  accountNumber: PropTypes.string.isRequired,
  customer: PropTypes.string.isRequired,
};

function ReplaceAllConfirm({find, replace, number}) {
  return <p>
    <FormattedMessage id='tools.sure.want.replace-all'
                      values={{
                        old: <Callout>{find}</Callout>,
                        new: <Callout>{replace}</Callout>,
                        n: <Callout><FormattedNumber value={number}/></Callout>,
                      }}/>
  </p>;
}

ReplaceAllConfirm.propTypes = {
  find: PropTypes.string.isRequired,
  replace: PropTypes.string.isRequired,
  number: PropTypes.number.isRequired,
};

function PreviewResult({name, idKey, value, preview, replace, star, flag, field}) {
  return <tr>
    <td>{flag}{star}</td>
    <td><a href={`/#customer/${idKey}`}>{name}</a></td>
    <td>{field}</td>
    <td>{value}</td>
    {preview ? <td>{preview}</td> : null}
    {preview && replace ? <td>
      <button className='btn text-button'
              style={{padding: 0}}
              onClick={replace}><FormattedMessage id="general.replace"/></button>
    </td> : null}
  </tr>;
}

PreviewResult.propTypes = {
  idKey: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  preview: PropTypes.string,
  field: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  replace: PropTypes.func,
};

export default injectIntl(FindReplacePage);
